import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP, p as pMod } from '../i18n/useP';
import {
  listPages, createPage, updatePage, deletePage, getPageView,
  listReports, createReport, updateReport, deleteReport, reorderReports,
  getReport, getCatalog,
  addLine, updateLine, deleteLine, reorderLines,
} from '../api/customReports';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import UnitSwitch from '../components/UnitSwitch';
import Card from '../components/Card';
import { useHubExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';
import PrintReportDocument from './PrintReportDocument';
import { CompareTable } from './FinancialsSynthesisSimple';
import { CustomReportPageTables } from './CustomReportsExport';
import { usePersistentValue } from '../hooks/usePersistentState';
import './CustomReportsPage.css';
import './ForecastSummary.css';

const ADD_PAGE = '__add_page__';

// Étiquette de colonne : ajoute l'année (sur 2 chiffres) si la fenêtre franchit 2 ans.
function colLabel(col, multiYear) {
  return multiYear ? `${col.label} ${String(col.year).slice(2)}` : col.label;
}

export default function CustomReportsPage({ activeTenant, addToast, year }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;

  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = usePersistentValue(`custrep_page_${tenantId}`, null);
  const [pageView, setPageView] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [unit, setUnit] = usePersistentValue(`custrep_unit_${tenantId}`, 'kchf');

  const [editingId, setEditingId] = useState(null);   // rapport ouvert dans le wizard
  const [editingDef, setEditingDef] = useState(null); // définition (lignes) du rapport édité

  const [creatingPage, setCreatingPage] = useState(false);
  const [pageName, setPageName] = useState('');
  const [renamingPage, setRenamingPage] = useState(false);
  const [pageRename, setPageRename] = useState('');
  const [showPrint, setShowPrint] = useState(false);

  const toast = useCallback((msg, kind) => { if (addToast) addToast(msg, kind); }, [addToast]);

  /* ── Chargements ──────────────────────────────────────────────────────── */
  const refreshPages = useCallback(async () => {
    if (!tenantId) return;
    const r = await listPages(tenantId);
    if (r.error) { toast(r.error, 'error'); return; }
    setPages(r.data || []);
  }, [tenantId, toast]);

  useEffect(() => {
    if (!pages.length) { if (activePageId) setActivePageId(null); return; }
    if (!activePageId || !pages.some((p) => p.id === activePageId)) setActivePageId(pages[0].id);
    // eslint-disable-next-line
  }, [pages]);

  const refreshPageView = useCallback(async () => {
    if (!tenantId || !activePageId) { setPageView(null); return; }
    const v = await getPageView(tenantId, activePageId, year);
    if (v.error) { toast(v.error, 'error'); setPageView(null); }
    else setPageView(v.data);
  }, [tenantId, activePageId, year, toast]);

  const refreshUnassigned = useCallback(async () => {
    if (!tenantId) { setUnassigned([]); return; }
    const r = await listReports(tenantId, 'none');
    if (!r.error) setUnassigned(r.data || []);
  }, [tenantId]);

  useEffect(() => { refreshPages(); }, [refreshPages]);
  useEffect(() => { refreshPageView(); }, [refreshPageView]);
  useEffect(() => { refreshUnassigned(); }, [refreshUnassigned, activePageId]);
  useEffect(() => {
    if (!tenantId) return;
    getCatalog(tenantId, year).then((r) => { if (!r.error) setCatalog(r.data?.atoms || []); });
  }, [tenantId, year]);

  const reloadDef = useCallback(async (reportId) => {
    const r = await getReport(tenantId, reportId);
    if (!r.error) setEditingDef(r.data);
    return r.data;
  }, [tenantId]);

  /* ── Pages ────────────────────────────────────────────────────────────── */
  const doCreatePage = async () => {
    const name = pageName.trim();
    if (!name) return;
    const r = await createPage(tenantId, name);
    if (r.error) { toast(r.error, 'error'); return; }
    setPageName(''); setCreatingPage(false);
    await refreshPages();
    setActivePageId(r.data.id);
  };
  const doRenamePage = async () => {
    const name = pageRename.trim();
    if (!name) { setRenamingPage(false); return; }
    const r = await updatePage(tenantId, activePageId, name);
    if (r.error) { toast(r.error, 'error'); return; }
    setRenamingPage(false);
    await refreshPages();
  };
  const doDeletePage = async () => {
    const page = pages.find((p) => p.id === activePageId);
    if (!page) return;
    if (!window.confirm(p('Supprimer la page « {{name}} » ? Ses rapports repassent en « à placer » (non supprimés).').replace('{{name}}', page.name))) return;
    const r = await deletePage(tenantId, activePageId);
    if (r.error) { toast(r.error, 'error'); return; }
    setActivePageId(null);
    await refreshPages();
    await refreshUnassigned();
  };

  /* ── Rapports (cards) ─────────────────────────────────────────────────── */
  const openWizard = async (reportId) => { await reloadDef(reportId); setEditingId(reportId); };
  const closeWizard = async () => { setEditingId(null); setEditingDef(null); await refreshPageView(); await refreshUnassigned(); };

  const addReportToPage = async () => {
    const r = await createReport(tenantId, { name: p('Nouveau rapport'), page_id: activePageId, width: 'full', period: 'calendar' });
    if (r.error) { toast(r.error, 'error'); return; }
    await refreshPageView();
    setEditingDef(r.data); setEditingId(r.data.id);
  };
  const assignToPage = async (reportId) => {
    const r = await updateReport(tenantId, reportId, { page_id: activePageId });
    if (r.error) { toast(r.error, 'error'); return; }
    await refreshPageView(); await refreshUnassigned();
  };
  const patchReport = async (reportId, patch) => {
    const r = await updateReport(tenantId, reportId, patch);
    if (r.error) { toast(r.error, 'error'); return; }
    if (editingId === reportId) setEditingDef(r.data);
    await refreshPageView();
  };
  const removeReport = async (reportId, name) => {
    if (!window.confirm(p('Supprimer le rapport « {{name}} » ?').replace('{{name}}', name))) return;
    const r = await deleteReport(tenantId, reportId);
    if (r.error) { toast(r.error, 'error'); return; }
    if (editingId === reportId) { setEditingId(null); setEditingDef(null); }
    await refreshPageView(); await refreshUnassigned();
  };
  const moveCard = async (idx, dir) => {
    const ids = (pageView?.cards || []).map((c) => c.report_id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await reorderReports(tenantId, ids);
    await refreshPageView();
  };

  /* ── Lignes (depuis le wizard) ────────────────────────────────────────── */
  const afterLineChange = async (resp) => {
    if (resp.error) { toast(resp.error, 'error'); return; }
    setEditingDef(resp.data);
    await refreshPageView();
  };
  const onAddLine = async (kind, extra = {}) => {
    const label = extra.label ?? (kind === 'header' ? p('Nouveau bloc') : kind === 'formula' ? p('Ligne calculée') : p('Nouvelle ligne'));
    const payload = { kind, label, ...extra };
    if (kind === 'formula' && payload.formula == null) payload.formula = '';
    await afterLineChange(await addLine(tenantId, editingId, payload));
  };
  const onPatchLine = async (lineId, patch) => afterLineChange(await updateLine(tenantId, editingId, lineId, patch));
  const onDeleteLine = async (lineId) => afterLineChange(await deleteLine(tenantId, editingId, lineId));
  const onMoveLine = async (idx, dir) => {
    const ids = (editingDef?.lines || []).map((l) => l.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await afterLineChange(await reorderLines(tenantId, editingId, ids));
  };

  /* ── Rendu ────────────────────────────────────────────────────────────── */
  const pageTabs = useMemo(
    () => [...pages.map((page) => ({ id: page.id, label: page.name })), { id: ADD_PAGE, label: '＋' }],
    [pages],
  );
  const onTabChange = (id) => {
    if (id === ADD_PAGE) { setCreatingPage(true); setPageName(''); return; }
    setActivePageId(id); setEditingId(null); setEditingDef(null); setCreatingPage(false); setRenamingPage(false);
  };

  /* Exports (PDF · Excel) — TOUTES les pages de la section, comme les autres
     hubs. Déclarés au registre : ils sortent par l'icône télécharger de la
     barre supérieure. Rien à exporter tant qu'aucune page n'est ouverte. */
  const excel = useHubExcelExport({
    tabs: pages.map((page) => ({ label: page.name, render: () => <CustomReportPageTables tenantId={tenantId} pageId={page.id} year={year} unit="chf" /> })),
    fileBase: 'Focus+',
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const canExport = Boolean(activePageId) && !creatingPage;
  useRegisterPageExports(useMemo(() => (canExport ? [
    { id: 'pdf', label: 'PDF', run: () => setShowPrint(true) },
    { id: 'excel', label: 'Excel', run: excel.run },
  ] : []), [canExport, excel.run]));

  const aside = canExport ? (
    <div className="custrep-tabaside">
      <button className="custrep-btn primary" onClick={addReportToPage}>{p('+ Rapport')}</button>
      <button className="custrep-link" onClick={() => { setPageRename(pages.find((page) => page.id === activePageId)?.name || ''); setRenamingPage(true); }}>{t('common:actions.rename')}</button>
      <button className="custrep-link danger" onClick={doDeletePage}>{t('common:actions.delete')}</button>
      <UnitSwitch value={unit} onChange={setUnit} />
    </div>
  ) : null;

  return (
    <PageShell
      framed
      title={t('cockpit:focusPlus')}
    >
      <Tabs tabs={pageTabs} active={activePageId || ''} onChange={onTabChange} aside={aside} />

      <div className="page-shell__panel">
        {creatingPage && (
          <div className="custrep-createbar">
            <input
              autoFocus className="custrep-input" placeholder={p('Nom de la page')}
              value={pageName} onChange={(e) => setPageName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doCreatePage(); if (e.key === 'Escape') { setCreatingPage(false); setPageName(''); } }}
            />
            <button className="custrep-btn primary" onClick={doCreatePage}>{p('Créer la page')}</button>
            <button className="custrep-btn" onClick={() => { setCreatingPage(false); setPageName(''); }}>{t('common:actions.cancel')}</button>
          </div>
        )}

        {!creatingPage && !activePageId && (
          <div className="custrep-empty">
            <p>{p('Aucune page. Clique « ＋ » pour créer un tableau de bord, puis ajoute des rapports-cards.')}</p>
          </div>
        )}

        {!creatingPage && activePageId && (
          <>
            {renamingPage && (
              <div className="custrep-createbar">
                <input
                  autoFocus className="custrep-input" value={pageRename}
                  onChange={(e) => setPageRename(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') doRenamePage(); if (e.key === 'Escape') setRenamingPage(false); }}
                />
                <button className="custrep-btn primary" onClick={doRenamePage}>OK</button>
                <button className="custrep-btn" onClick={() => setRenamingPage(false)}>{t('common:actions.cancel')}</button>
              </div>
            )}

            <div className="custrep-reports">
              {(pageView?.cards || []).map((card, idx) => (
                <ReportBlock
                  key={card.report_id}
                  card={card}
                  idx={idx}
                  count={(pageView?.cards || []).length}
                  unit={unit}
                  onEdit={() => openWizard(card.report_id)}
                  onDelete={() => removeReport(card.report_id, card.name)}
                  onMove={(dir) => moveCard(idx, dir)}
                />
              ))}
              {(pageView?.cards || []).length === 0 && (
                <div className="custrep-empty">
                  <p>{p('Page vide. Clique « + Rapport » pour ajouter un rapport.')}</p>
                </div>
              )}
            </div>

            {unassigned.length > 0 && (
              <div className="custrep-unassigned">
                <p className="custrep-unassigned__title">{p('À placer')} ({unassigned.length})</p>
                {unassigned.map((r) => (
                  <span key={r.id} className="custrep-pill">
                    {r.name}
                    <button className="custrep-link" onClick={() => assignToPage(r.id)}>{p('ajouter ici')}</button>
                    <button className="custrep-link danger" onClick={() => removeReport(r.id, r.name).then(refreshUnassigned)}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {editingId && editingDef && (
        <ReportWizard
          def={editingDef}
          catalog={catalog}
          onPatchReport={(patch) => patchReport(editingId, patch)}
          onAddLine={onAddLine}
          onPatchLine={onPatchLine}
          onDeleteLine={onDeleteLine}
          onMoveLine={onMoveLine}
          onClose={closeWizard}
        />
      )}

      {excel.stage}
      {showPrint && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={year}
          reports={['custom-reports']}
          mode="pdf"
          onClose={() => setShowPrint(false)}
        />
      )}
    </PageShell>
  );
}


/* ── Bloc rapport (lecture) — format minimaliste de la synthèse prévisionnelle
   (en-tête serif + CompareTable : bande de groupe, colonne Total phare, négatifs
   en terracotta, sous-totaux en gras navy). ────────────────────────────────── */
/* Transforme une card (vue serveur : columns + rows) en entrée CompareTable
   { groups, rows }. Partagé par l'écran (ReportBlock) et les exports PDF/Excel
   (CustomReportsExport) — source unique du mapping. */
export function buildReportTable(card) {
  const columns = card.columns || [];
  const multiYear = new Set(columns.map((c) => c.year)).size > 1;
  const totalLabel = card.period === 'rolling12' ? pMod('Total 12 m') : `Total ${columns[0]?.year ?? ''}`;
  const groupLabel = card.period === 'rolling12' ? pMod('12 mois roulants') : `${columns[0]?.year ?? ''}`;

  const groups = [{
    label: groupLabel,
    cols: [
      ...columns.map((c) => ({ key: c.key, label: colLabel(c, multiYear) })),
      { key: '__tot', label: totalLabel, highlight: true },
    ],
  }];

  // CompareTable attend des pourcentages déjà en points (ex. 20) ; le rapport
  // les stocke en fraction décimale (0.2 = 20 %) → ×100 pour les lignes en %.
  const rows = (card.rows || []).map((r) => {
    if (r.kind === 'header') return { kind: 'header', label: r.label };
    const isPct = r.value_format === 'percent';
    const scale = (v) => (isPct && v != null && Number.isFinite(Number(v)) ? Number(v) * 100 : v);
    const out = {
      label: r.error
        ? <>{r.label}<span className="custrep-err" title={r.error}> ⚠</span></>
        : r.label,
      fmt: isPct ? 'pct' : undefined,
      kind: r.emphasis ? 'sub' : undefined,
      __tot: scale(r.total),
    };
    columns.forEach((c) => { out[c.key] = scale(r.values?.[c.key]); });
    return out;
  });
  return { groups, rows, columns };
}

function ReportBlock({ card, idx, count, unit, onEdit, onDelete, onMove }) {
  const { t } = useTranslation();
  const p = useP();
  const { groups, rows, columns } = buildReportTable(card);

  return (
    <Card
      className="report-card custrep-report"
      title={card.name}
      actions={
        <span className="custrep-cardact">
          <button className="custrep-mini" disabled={idx === 0} onClick={() => onMove(-1)} title={p('Monter')}>▲</button>
          <button className="custrep-mini" disabled={idx === count - 1} onClick={() => onMove(1)} title={p('Descendre')}>▼</button>
          <button className="custrep-link" onClick={onEdit}>{p('Construire')}</button>
          <button className="custrep-link danger" onClick={onDelete} title={p('Supprimer le rapport')}>{t('common:actions.delete')}</button>
        </span>
      }
    >
      {columns.length === 0 ? (
        <p className="custrep-note">{p('Aucun mois clôturé — rien à afficher.')}</p>
      ) : rows.length === 0 ? (
        <p className="custrep-note">{p('Rapport vide — clique « Construire ».')}</p>
      ) : (
        <CompareTable groups={groups} rows={rows} unit={unit} compact />
      )}
    </Card>
  );
}

/* ── Wizard (popover multi-étapes) ───────────────────────────────────────── */
const STEP_KEYS = ['Rapport', 'Période', 'Comptes', 'Calculs'];

function ReportWizard({ def, catalog, onPatchReport, onAddLine, onPatchLine, onDeleteLine, onMoveLine, onClose }) {
  const p = useP();
  const [step, setStep] = useState(0);
  const lines = def.lines || [];
  const groups = useMemo(() => {
    const by = {};
    catalog.forEach((a) => { (by[a.statement_label] = by[a.statement_label] || []).push(a); });
    return by;
  }, [catalog]);
  const globalIdx = (id) => lines.findIndex((l) => l.id === id);

  const steps = STEP_KEYS.map((s) => p(s));
  const STEP_HINT = [
    p('Nom du rapport.'),
    p('Colonnes affichées (mois).'),
    p('Postes à afficher — cherche un agrégat, un compte ou un sous-total.'),
    p('Lignes calculées à partir des postes ci-dessus (formule libre).'),
  ];

  return (
    <div className="custrep-modal__overlay">
      <div className="custrep-modal custrep-modal--rail" onClick={(e) => e.stopPropagation()}>
        <div className="custrep-modal__head">
          <span className="custrep-modal__title">{def.name || p('Rapport')}</span>
          <button className="custrep-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="custrep-wrap">
          {/* Rail vertical — pastilles numérotées (terracotta = étape active). */}
          <nav className="custrep-rail">
            {steps.map((s, i) => (
              <button
                key={STEP_KEYS[i]}
                className={`custrep-railstep${i === step ? ' on' : ''}${i < step ? ' done' : ''}`}
                onClick={() => setStep(i)}
              >
                <span className="custrep-railstep__n">{i + 1}</span>
                <span className="custrep-railstep__lab">{s}</span>
              </button>
            ))}
          </nav>

          <div className="custrep-pane">
            <div className="custrep-pane__hint">{STEP_HINT[step]}</div>
            <div className="custrep-modal__body">
              {step === 0 && <StepReport def={def} onPatch={onPatchReport} />}
              {step === 1 && <StepPeriod def={def} onPatch={onPatchReport} />}
              {step === 2 && (
                <StepAccounts
                  lines={lines} groups={groups} globalIdx={globalIdx}
                  onAddLine={onAddLine} onPatchLine={onPatchLine} onDeleteLine={onDeleteLine} onMoveLine={onMoveLine}
                />
              )}
              {step === 3 && (
                <StepCalc
                  lines={lines} globalIdx={globalIdx}
                  onAddLine={onAddLine} onPatchLine={onPatchLine} onDeleteLine={onDeleteLine} onMoveLine={onMoveLine}
                />
              )}
            </div>

            <div className="custrep-modal__foot">
              <button className="custrep-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>{p('← Précédent')}</button>
              <span className="custrep-modal__hint">{p('Enregistré au fur et à mesure.')}</span>
              {step < steps.length - 1
                ? <button className="custrep-btn primary" onClick={() => setStep((s) => s + 1)}>{p('Suivant →')}</button>
                : <button className="custrep-btn primary" onClick={onClose}>{p('Terminer')}</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Typeahead de sélection d'un poste (agrégat / compte / sous-total). Champ
   toujours visible : on tape, les propositions filtrent en direct. */
const ATOM_KIND_KEYS = { section: 'Agrégat', 'sous-total': 'Sous-total', ligne: 'Compte' };
function AccountPicker({ groups, onPick }) {
  const { t } = useTranslation();
  const p = useP();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const atoms = useMemo(() => Object.values(groups).flat(), [groups]);
  const atomKind = (type) => (ATOM_KIND_KEYS[type] ? p(ATOM_KIND_KEYS[type]) : type);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s
      ? atoms.filter((a) => a.label.toLowerCase().includes(s)
          || (a.section_label || '').toLowerCase().includes(s)
          || (a.statement_label || '').toLowerCase().includes(s)
          || (ATOM_KIND_KEYS[a.type_label] || '').toLowerCase().includes(s)
          || atomKind(a.type_label).toLowerCase().includes(s))
      : atoms;
    return base.slice(0, 50);
  }, [q, atoms, p]);

  const choose = (a) => { if (a) { onPick(a.ref); setQ(''); setOpen(false); setHi(0); } };

  return (
    <div className="custrep-picker">
      <input
        className="custrep-input grow" placeholder={t('phrases:p_61c5756f0e')}
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); choose(matches[hi]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && matches.length > 0 && (
        <div className="custrep-suggest">
          {matches.map((a, i) => (
            <button
              key={a.ref} type="button"
              className={`custrep-suggest__it${i === hi ? ' hi' : ''}`}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(a); }}
            >
              <span className={`custrep-atomkind k-${a.type_label}`}>{atomKind(a.type_label)}</span>
              <span className="custrep-suggest__lab">{a.label}</span>
              <span className="custrep-suggest__meta">{a.statement_label}{a.section_label && a.section_label !== a.label ? ` · ${a.section_label}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Step 1 — nom + largeur */
function StepReport({ def, onPatch }) {
  const p = useP();
  const [name, setName] = useState(def.name);
  useEffect(() => { setName(def.name); }, [def.name]);
  return (
    <div className="custrep-form">
      <label className="custrep-field">
        <span className="custrep-field__lab">{p('Nom du rapport')}</span>
        <input
          className="custrep-input grow" value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { if (name.trim() && name !== def.name) onPatch({ name: name.trim() }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </label>
    </div>
  );
}

/* Step 2 — période */
function StepPeriod({ def, onPatch }) {
  const p = useP();
  return (
    <div className="custrep-form">
      <div className="custrep-field">
        <span className="custrep-field__lab">{p('Période affichée (colonnes mensuelles)')}</span>
        <div className="custrep-choice col">
          <button className={`custrep-choicebtn${def.period === 'calendar' ? ' on' : ''}`} onClick={() => onPatch({ period: 'calendar' })}>
            <b>{p('Année courante — 12 mois')}</b>
            <span>{p("Jan → Déc de l'année sélectionnée. Mois non clôturés laissés vides.")}</span>
          </button>
          <button className={`custrep-choicebtn${def.period === 'rolling12' ? ' on' : ''}`} onClick={() => onPatch({ period: 'rolling12' })}>
            <b>{p('12 mois roulants')}</b>
            <span>{p("Les 12 derniers mois clôturés (peut franchir l'année).")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* Step 3 — sélection & ordre des comptes (source + titres) */
function StepAccounts({ lines, groups, globalIdx, onAddLine, onPatchLine, onDeleteLine, onMoveLine }) {
  const p = useP();
  const rows = lines.filter((l) => l.kind === 'source' || l.kind === 'header');

  const addAccount = (ref) => {
    if (!ref) return;
    const atom = Object.values(groups).flat().find((a) => a.ref === ref);
    onAddLine('source', { source_ref: ref, label: atom?.label || p('Compte') });
  };

  return (
    <div className="custrep-form">
      <div className="custrep-addbar">
        <AccountPicker groups={groups} onPick={addAccount} />
        <button className="custrep-btn" onClick={() => onAddLine('header')}>{p('+ Titre de bloc')}</button>
      </div>

      {rows.length === 0 && <p className="custrep-note">{p('Aucun poste. Cherche un agrégat, un compte ou un sous-total ci-dessus.')}</p>}

      {rows.map((ln) => (
        <AccountRow
          key={ln.id} line={ln} groups={groups}
          onPatch={(patch) => onPatchLine(ln.id, patch)}
          onDelete={() => onDeleteLine(ln.id)}
          onUp={() => onMoveLine(globalIdx(ln.id), -1)}
          onDown={() => onMoveLine(globalIdx(ln.id), 1)}
        />
      ))}
    </div>
  );
}

function AccountRow({ line, groups, onPatch, onDelete, onUp, onDown }) {
  const { t } = useTranslation();
  const p = useP();
  const [label, setLabel] = useState(line.label);
  useEffect(() => { setLabel(line.label); }, [line.label]);
  const isHeader = line.kind === 'header';
  return (
    <div className={`custrep-brow kind-${line.kind}`}>
      <div className="custrep-brow-move">
        <button className="custrep-mini" onClick={onUp} title={p('Monter')}>▲</button>
        <button className="custrep-mini" onClick={onDown} title={p('Descendre')}>▼</button>
      </div>
      {line.ref_key && <span className="custrep-refkey">[{line.ref_key}]</span>}
      {isHeader && <span className="custrep-tag">{p('Titre')}</span>}
      <input
        className="custrep-input grow" value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => { if (label !== line.label) onPatch({ label }); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      />
      {!isHeader && (
        <select className="custrep-source" value={line.source_ref || ''} onChange={(e) => onPatch({ source_ref: e.target.value })}>
          <option value="">{p('— compte —')}</option>
          {Object.entries(groups).map(([g, atoms]) => (
            <optgroup key={g} label={g}>
              {atoms.map((a) => (
                <option key={a.ref} value={a.ref}>{a.label}{a.section_label ? ` · ${a.section_label}` : ''}</option>
              ))}
            </optgroup>
          ))}
        </select>
      )}
      <button className="custrep-mini danger" onClick={onDelete} title={t('common:actions.delete')}>✕</button>
    </div>
  );
}

/* Step 4 — lignes calculées (valeur / %) + formule */
function StepCalc({ lines, globalIdx, onAddLine, onPatchLine, onDeleteLine, onMoveLine }) {
  const p = useP();
  const rows = lines.filter((l) => l.kind === 'formula');
  const refables = lines.filter((l) => l.ref_key);

  return (
    <div className="custrep-form">
      <div className="custrep-addbar">
        <button className="custrep-btn primary" onClick={() => onAddLine('formula', { value_format: 'amount' })}>{p('+ Ligne calculée (valeur)')}</button>
        <button className="custrep-btn" onClick={() => onAddLine('formula', { value_format: 'percent' })}>{p('+ Ligne calculée (%)')}</button>
      </div>

      <p className="custrep-hint">
        {p('Référence une autre ligne par son jeton, ex.')} <code>[l1] + [l2]</code> {p("(les charges sont déjà négatives → une marge s'écrit avec +). Un ratio :")} <code>[l3] / [l1]</code>.
      </p>

      {rows.length === 0 && <p className="custrep-note">{p('Aucune ligne calculée.')}</p>}

      {rows.map((ln) => (
        <CalcRow
          key={ln.id} line={ln} refables={refables.filter((l) => l.id !== ln.id)}
          onPatch={(patch) => onPatchLine(ln.id, patch)}
          onDelete={() => onDeleteLine(ln.id)}
          onUp={() => onMoveLine(globalIdx(ln.id), -1)}
          onDown={() => onMoveLine(globalIdx(ln.id), 1)}
        />
      ))}
    </div>
  );
}

function CalcRow({ line, refables, onPatch, onDelete, onUp, onDown }) {
  const { t } = useTranslation();
  const p = useP();
  const [label, setLabel] = useState(line.label);
  const [formula, setFormula] = useState(line.formula || '');
  useEffect(() => { setLabel(line.label); }, [line.label]);
  useEffect(() => { setFormula(line.formula || ''); }, [line.formula]);
  const insertRef = (key) => {
    const next = `${formula}${formula && !formula.endsWith(' ') ? ' ' : ''}[${key}]`;
    setFormula(next); onPatch({ formula: next });
  };
  return (
    <div className="custrep-brow kind-formula">
      <div className="custrep-brow-move">
        <button className="custrep-mini" onClick={onUp} title={p('Monter')}>▲</button>
        <button className="custrep-mini" onClick={onDown} title={p('Descendre')}>▼</button>
      </div>
      {line.ref_key && <span className="custrep-refkey">[{line.ref_key}]</span>}
      <div className="custrep-calcmain">
        <div className="custrep-calcrow1">
          <input
            className="custrep-input grow" placeholder={t('phrases:p_587f6cc827')} value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => { if (label !== line.label) onPatch({ label }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
          <select className="custrep-fmt" value={line.value_format} onChange={(e) => onPatch({ value_format: e.target.value })} title={p('Format')}>
            <option value="amount">{p('Valeur (CHF)')}</option>
            <option value="percent">{p('Pourcentage (%)')}</option>
          </select>
          <label className="custrep-emph"><input type="checkbox" checked={!!line.emphasis} onChange={(e) => onPatch({ emphasis: e.target.checked })} />{p('gras')}</label>
          <button className="custrep-mini danger" onClick={onDelete} title={t('common:actions.delete')}>✕</button>
        </div>
        <input
          className="custrep-input grow mono" placeholder={p('Formule, ex. [l1] + [l2]')} value={formula}
          onChange={(e) => setFormula(e.target.value)}
          onBlur={() => { if (formula !== (line.formula || '')) onPatch({ formula }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
        <div className="custrep-chips">
          {refables.map((l) => (
            <button key={l.id} className="custrep-chip" title={l.label} onClick={() => insertRef(l.ref_key)}>
              [{l.ref_key}] {l.label}
            </button>
          ))}
          {refables.length === 0 && <span className="custrep-note">{p("Ajoute d'abord des comptes (étape Comptes) pour les référencer.")}</span>}
        </div>
      </div>
    </div>
  );
}
